import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  Upload,
  Download,
  RotateCcw,
  Maximize2,
  Save,
  LayoutList,
  Lock,
  Unlock,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const TOKEN_KEY = "badgeAdminToken";
const DEFAULT_PHOTO = null; // start empty — user uploads their own

// Generate a fresh unique employee ID. Format: `HC-` + base-36 timestamp
// (last 4 chars → ~1.7 million combos, effectively no collisions in a
// small-org registry) + a 2-char random suffix. Example: `HC-K7M9-3F`.
const generateEmployeeId = () => {
  const stamp = Date.now().toString(36).toUpperCase().slice(-4);
  const rand = Math.floor(Math.random() * 46656) // 36^3
    .toString(36)
    .toUpperCase()
    .padStart(3, "0")
    .slice(0, 2);
  return `HC-${stamp}-${rand}`;
};
const LOGO_WHITE = "/brand/hepcure-logo-white.png";
const VERIFY_ORIGIN =
  process.env.REACT_APP_BACKEND_URL || window.location.origin;

const DEFAULTS = {
  name: "Sample Worker",
  department: "OUTREACH & SCREENING",
  idNumber: "HC-000123",
  role: "OUTREACH WORKER",
  issued: "2026-01-15",
  expires: "2027-01-14",
};

// Zazzle CR80 portrait ID badge — 2.125" x 3.375" (aspect 1 : 1.588).
// Outer card dimensions are LOCKED so the print format never shifts.
// Exported at 8x pixel ratio = 3200 x 5088 PNG (~900 DPI at print size).
const BADGE_W = 400;
const BADGE_ASPECT = 3.375 / 2.125;
const BADGE_H = Math.round(BADGE_W * BADGE_ASPECT); // 636
// Photo frame is a fixed slightly-portrait aspect that leaves room for
// the top band, name, info block, and bottom banner within CR80.
const PHOTO_FRAME_ASPECT = 1.0; // square frame fits perfectly in CR80
const EXPORT_PIXEL_RATIO = 8;

export default function Badge() {
  const [photo, setPhoto] = useState(DEFAULT_PHOTO);
  const [photoAspect, setPhotoAspect] = useState(1.0); // w/h — updated on load
  // Give this session a freshly-minted default ID up-front so a brand-new
  // record never collides with the "HC-000123" seed employee (or with any
  // previously-saved record). When the user navigates in with `?edit=<id>`,
  // this default is immediately overwritten below.
  const [fields, setFields] = useState(() => ({
    ...DEFAULTS,
    idNumber: generateEmployeeId(),
  }));
  const [photoZoom, setPhotoZoom] = useState(1);
  const [photoOffset, setPhotoOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const badgeRef = useRef(null);
  const fileRef = useRef(null);
  const dragStartRef = useRef({ x: 0, y: 0, ox: 0, oy: 0 });
  const pinchStartRef = useRef(null);
  const [downloading, setDownloading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [photoLocked, setPhotoLocked] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const editId = searchParams.get("edit");

  const resetPhotoTransform = () => {
    setPhotoZoom(1);
    setPhotoOffset({ x: 0, y: 0 });
  };

  // Load an existing record when navigated to /badge?edit=HC-000123
  useEffect(() => {
    if (!editId) return;
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;
    fetch(`${API}/badge/employees/${editId}`, {
      headers: { "X-Auth-Token": token },
    })
      .then((r) => (r.ok ? r.json() : null))
      .then((emp) => {
        if (!emp) return;
        setFields({
          name: emp.name || "",
          program: emp.program || "",
          department: emp.department || "",
          idNumber: emp.id_number || "",
          role: emp.role || "",
          issued: emp.issued || "",
          expires: emp.expires || "",
        });
        if (emp.photo_url) setPhoto(emp.photo_url);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // Measure the natural aspect of the currently displayed photo so the
  // frame can shrink to hug it (no letterbox side-padding).
  useEffect(() => {
    if (!photo) return;
    const img = new Image();
    img.onload = () => {
      if (img.naturalWidth && img.naturalHeight) {
        setPhotoAspect(img.naturalWidth / img.naturalHeight);
      }
    };
    img.src = photo;
  }, [photo]);

  const onPhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPhoto(ev.target.result);
      resetPhotoTransform();
    };
    reader.readAsDataURL(file);
  };

  const resetPhoto = () => {
    setPhoto(DEFAULT_PHOTO);
    resetPhotoTransform();
    if (fileRef.current) fileRef.current.value = "";
  };

  const clampZoom = (z) => Math.min(4, Math.max(0.3, z));

  // Global mouse/touch listeners so dragging + pinching keep working
  // even when the pointer leaves the photo frame.
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => {
      if (e.touches && e.touches.length >= 2 && pinchStartRef.current) {
        // Pinch-zoom: adjust zoom by finger distance ratio.
        e.preventDefault();
        const [a, b] = [e.touches[0], e.touches[1]];
        const dist = Math.hypot(
          b.clientX - a.clientX,
          b.clientY - a.clientY
        );
        const start = pinchStartRef.current;
        const ratio = dist / start.dist;
        setPhotoZoom(clampZoom(start.zoom * ratio));
        return;
      }
      const p = e.touches ? e.touches[0] : e;
      const dx = p.clientX - dragStartRef.current.x;
      const dy = p.clientY - dragStartRef.current.y;
      setPhotoOffset({
        x: dragStartRef.current.ox + dx,
        y: dragStartRef.current.oy + dy,
      });
    };
    const onUp = () => {
      setIsDragging(false);
      pinchStartRef.current = null;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [isDragging]);

  const onDragStart = (e) => {
    if (photoLocked) return;
    // Two-finger touch → pinch zoom
    if (e.touches && e.touches.length >= 2) {
      const [a, b] = [e.touches[0], e.touches[1]];
      const dist = Math.hypot(
        b.clientX - a.clientX,
        b.clientY - a.clientY
      );
      pinchStartRef.current = { dist, zoom: photoZoom };
      setIsDragging(true);
      return;
    }
    const p = e.touches ? e.touches[0] : e;
    dragStartRef.current = {
      x: p.clientX,
      y: p.clientY,
      ox: photoOffset.x,
      oy: photoOffset.y,
    };
    setIsDragging(true);
  };

  // Mouse wheel zoom on the frame (desktop).
  const onWheelZoom = (e) => {
    if (photoLocked) return;
    e.preventDefault();
    const delta = -e.deltaY * 0.0015;
    setPhotoZoom((z) => clampZoom(z + delta));
  };

  // Draw the full badge to a canvas by walking key DOM elements and
  // rendering their computed positions/styles natively. This avoids all
  // DOM-rasterization libraries (html-to-image, html2canvas), which have
  // been unreliable on iOS Safari with nested CSS transforms.
  //
  // Only the visual layers that actually appear on the printed badge are
  // drawn: card, top band, lanyard slot, logo, photo (with zoom/pan),
  // name, department, QR code, bottom banner (scan hint + role).
  const drawBadgeToCanvas = async () => {
    const badge = badgeRef.current;
    if (!badge) return null;

    // Ensure webfonts (Outfit / Plus Jakarta) have finished loading before
    // we measure text — otherwise the fallback system font renders in the
    // canvas even though the DOM will use the webfont.
    if (document.fonts?.ready) {
      try { await document.fonts.ready; } catch { /* ignore */ }
    }

    const badgeRect = badge.getBoundingClientRect();
    const rectOf = (sel) => {
      const el = badge.querySelector(`[data-testid="${sel}"]`);
      if (!el) return null;
      const r = el.getBoundingClientRect();
      return {
        el,
        x: r.left - badgeRect.left,
        y: r.top - badgeRect.top,
        w: r.width,
        h: r.height,
      };
    };

    // Helper to draw a rounded rectangle path (Safari 15 lacks canvas.roundRect).
    const roundedPath = (ctx, x, y, w, h, r) => {
      const rr = Math.min(r, w / 2, h / 2);
      ctx.beginPath();
      ctx.moveTo(x + rr, y);
      ctx.arcTo(x + w, y, x + w, y + h, rr);
      ctx.arcTo(x + w, y + h, x, y + h, rr);
      ctx.arcTo(x, y + h, x, y, rr);
      ctx.arcTo(x, y, x + w, y, rr);
      ctx.closePath();
    };

    const PR = EXPORT_PIXEL_RATIO; // 8× → 3200×5088 PNG at CR80 print size
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(BADGE_W * PR);
    canvas.height = Math.round(BADGE_H * PR);
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.scale(PR, PR); // work in CSS-pixel space

    // 1. Card background (white, rounded 22) + clip everything to card
    ctx.fillStyle = "#ffffff";
    roundedPath(ctx, 0, 0, BADGE_W, BADGE_H, 22);
    ctx.fill();
    ctx.save();
    roundedPath(ctx, 0, 0, BADGE_W, BADGE_H, 22);
    ctx.clip();

    // 2. Top navy band (height matches DOM, use DOM rect for accuracy)
    const topBand = rectOf("badge-top-band");
    if (topBand) {
      const g = ctx.createLinearGradient(0, topBand.y, 0, topBand.y + topBand.h);
      g.addColorStop(0, "#1F4E79");
      g.addColorStop(1, "#183F63");
      ctx.fillStyle = g;
      ctx.fillRect(topBand.x, topBand.y, topBand.w, topBand.h);
    }

    // 3. Lanyard slot (white pill)
    const lanyard = rectOf("badge-lanyard-slot");
    if (lanyard) {
      ctx.fillStyle = "#ffffff";
      roundedPath(ctx, lanyard.x, lanyard.y, lanyard.w, lanyard.h, 8);
      ctx.fill();
    }

    // 4. HepCURE logo — draw a freshly-loaded copy so drawImage never
    // silently no-ops on a partially-decoded element in Safari.
    const logoRect = rectOf("badge-logo");
    if (logoRect) {
      try {
        const logoImg = new Image();
        logoImg.crossOrigin = "anonymous";
        logoImg.src = LOGO_WHITE;
        await new Promise((res, rej) => {
          if (logoImg.complete && logoImg.naturalWidth > 0) return res();
          logoImg.onload = () => res();
          logoImg.onerror = rej;
        });
        if (logoImg.decode) { try { await logoImg.decode(); } catch { /* ignore */ } }
        ctx.drawImage(logoImg, logoRect.x, logoRect.y, logoRect.w, logoRect.h);
      } catch { /* logo optional — swallow */ }
    }

    // 5. Photo frame — border, background, and cropped photo (with the
    // exact zoom/pan transform that capturePhotoDataUrl already handles).
    const frame = rectOf("badge-photo-frame");
    if (frame) {
      const borderW = 3;
      // Frame background fill (inside border)
      ctx.fillStyle = "#f1f5f9";
      roundedPath(
        ctx,
        frame.x + borderW,
        frame.y + borderW,
        frame.w - borderW * 2,
        frame.h - borderW * 2,
        3,
      );
      ctx.fill();

      // Draw the photo (if present) with the same transform math as save.
      if (photo) {
        const srcImg = new Image();
        srcImg.src = photo;
        try {
          await new Promise((res, rej) => {
            if (srcImg.complete && srcImg.naturalWidth > 0) return res();
            srcImg.onload = () => res();
            srcImg.onerror = rej;
          });
          if (srcImg.decode) { try { await srcImg.decode(); } catch { /* ignore */ } }

          const innerW = frame.w - borderW * 2;
          const innerH = frame.h - borderW * 2;

          ctx.save();
          // Clip to inner rounded rect
          roundedPath(ctx, frame.x + borderW, frame.y + borderW, innerW, innerH, 3);
          ctx.clip();

          // Move origin to inner top-left, then apply CSS transform:
          //   translate(pos.x, pos.y) scale(zoom); origin center center;
          ctx.translate(frame.x + borderW, frame.y + borderW);
          ctx.translate(photoOffset.x, photoOffset.y);
          ctx.translate(innerW / 2, innerH / 2);
          ctx.scale(photoZoom, photoZoom);
          ctx.translate(-innerW / 2, -innerH / 2);

          const nw = srcImg.naturalWidth;
          const nh = srcImg.naturalHeight;
          const imgA = nw / nh;
          const frameA = innerW / innerH;
          let dispW;
          let dispH;
          if (imgA > frameA) {
            dispW = innerW;
            dispH = innerW / imgA;
          } else {
            dispH = innerH;
            dispW = innerH * imgA;
          }
          ctx.drawImage(
            srcImg,
            0, 0, nw, nh,
            (innerW - dispW) / 2, (innerH - dispH) / 2, dispW, dispH,
          );
          ctx.restore();
        } catch { /* image failed — leave frame blank */ }
      }

      // Photo frame border (drawn on top for a crisp edge)
      ctx.strokeStyle = "#1F4E79";
      ctx.lineWidth = borderW;
      roundedPath(
        ctx,
        frame.x + borderW / 2,
        frame.y + borderW / 2,
        frame.w - borderW,
        frame.h - borderW,
        3,
      );
      ctx.stroke();
    }

    // 6. Info row — name + department (text) beside QR (in bordered box)
    const drawCenteredText = (rect, text, opts) => {
      if (!rect) return;
      const {
        font,
        color,
        letterSpacing = 0,
        baseline = "top",
      } = opts;
      ctx.save();
      ctx.font = font;
      ctx.fillStyle = color;
      ctx.textAlign = "center";
      ctx.textBaseline = baseline;
      // letterSpacing on CanvasRenderingContext2D is supported in Chrome 99+,
      // Safari 16.4+; harmless no-op on older engines.
      if ("letterSpacing" in ctx) ctx.letterSpacing = `${letterSpacing}px`;
      ctx.fillText(text, rect.x + rect.w / 2, rect.y);
      ctx.restore();
    };

    const nameRect = rectOf("badge-name-text");
    if (nameRect && fields.name) {
      const fam = getComputedStyle(nameRect.el).fontFamily;
      drawCenteredText(nameRect, fields.name, {
        font: `800 22px ${fam}`,
        color: "#1F4E79",
        letterSpacing: -0.22, // -0.01em × 22
      });
    }
    const deptRect = rectOf("badge-dept-text");
    if (deptRect && fields.department) {
      const fam = getComputedStyle(deptRect.el).fontFamily;
      drawCenteredText(deptRect, fields.department, {
        font: `700 13px ${fam}`,
        color: "#0f172a",
        letterSpacing: 0.52, // 0.04em × 13
      });
    }

    // QR box — border + rendered SVG
    const qrBox = rectOf("badge-qr-box");
    if (qrBox) {
      // Box background/border
      ctx.fillStyle = "#ffffff";
      roundedPath(ctx, qrBox.x, qrBox.y, qrBox.w, qrBox.h, 8);
      ctx.fill();
      ctx.strokeStyle = "#cbd5e1";
      ctx.lineWidth = 1;
      roundedPath(ctx, qrBox.x + 0.5, qrBox.y + 0.5, qrBox.w - 1, qrBox.h - 1, 8);
      ctx.stroke();

      // Serialize the QR <svg> to a blob and drawImage. Plain SVG paths,
      // no external refs → no CORS pitfalls.
      const qrSvgEl = qrBox.el.querySelector("svg");
      if (qrSvgEl) {
        try {
          const clone = qrSvgEl.cloneNode(true);
          // xmlns is required for the SVG to load as an <img>.
          if (!clone.getAttribute("xmlns")) {
            clone.setAttribute("xmlns", "http://www.w3.org/2000/svg");
          }
          const svgStr = new XMLSerializer().serializeToString(clone);
          const svgUrl =
            "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgStr);
          const svgImg = new Image();
          svgImg.src = svgUrl;
          await new Promise((res, rej) => {
            svgImg.onload = () => res();
            svgImg.onerror = rej;
          });
          if (svgImg.decode) { try { await svgImg.decode(); } catch { /* ignore */ } }
          // QR svg sits inside the qrBox with padding: 5
          const pad = 5;
          ctx.drawImage(
            svgImg,
            qrBox.x + pad,
            qrBox.y + pad,
            qrBox.w - pad * 2,
            qrBox.h - pad * 2,
          );
        } catch { /* QR failed — leave blank */ }
      }
    }

    // 7. Bottom banner (teal top-border + navy gradient + scan hint + role)
    const banner = rectOf("badge-bottom-banner");
    if (banner) {
      // Gradient background
      const bg = ctx.createLinearGradient(0, banner.y + banner.h, 0, banner.y);
      bg.addColorStop(0, "#1F4E79");
      bg.addColorStop(1, "#255F92");
      ctx.fillStyle = bg;
      ctx.fillRect(banner.x, banner.y, banner.w, banner.h);
      // Teal top-border (2px)
      ctx.fillStyle = "#6BA292";
      ctx.fillRect(banner.x, banner.y, banner.w, 2);
    }

    const scanRect = rectOf("badge-scan-hint");
    if (scanRect) {
      const fam = getComputedStyle(scanRect.el).fontFamily;
      drawCenteredText(scanRect, "SCAN QR TO VERIFY · hepcure.ca/verify", {
        font: `600 9px ${fam}`,
        color: "rgba(255,255,255,0.75)",
        letterSpacing: 1.62, // 0.18em × 9
      });
    }

    const roleRect = rectOf("badge-role-text");
    if (roleRect && fields.role) {
      const fam = getComputedStyle(roleRect.el).fontFamily;
      drawCenteredText(roleRect, fields.role, {
        font: `800 21px ${fam}`,
        color: "#ffffff",
        letterSpacing: 3.78, // 0.18em × 21
      });
    }

    ctx.restore(); // release the card clip
    return canvas;
  };

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const canvas = await drawBadgeToCanvas();
      if (!canvas) {
        toast.error("Could not render badge");
        return;
      }
      const filename = `hepcure-badge-${(fields.name || "employee")
        .replace(/\s+/g, "-")
        .toLowerCase()}.png`;

      // Prefer blob + object URL over data URL. iOS Safari struggles with
      // multi-MB `data:` URLs (the download dialog shows but tapping
      // "Download" hangs on the giant inline URL). Blob URLs let Safari
      // stream the file to Photos/Files just like any normal asset.
      const blob = await new Promise((resolve) => {
        if (canvas.toBlob) {
          canvas.toBlob((b) => resolve(b), "image/png");
        } else {
          // Fallback: convert data URL to blob manually.
          const url = canvas.toDataURL("image/png");
          const [meta, b64] = url.split(",");
          const mime = meta.match(/:(.*?);/)?.[1] || "image/png";
          const bin = atob(b64);
          const arr = new Uint8Array(bin.length);
          for (let i = 0; i < bin.length; i++) arr[i] = bin.charCodeAt(i);
          resolve(new Blob([arr], { type: mime }));
        }
      });
      if (!blob) {
        toast.error("Could not encode badge PNG");
        return;
      }

      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.download = filename;
      link.href = objectUrl;
      link.rel = "noopener";
      // iOS requires the anchor to be in the DOM at the moment of click.
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      // Revoke on next tick so Safari has time to consume the URL.
      setTimeout(() => URL.revokeObjectURL(objectUrl), 4000);
    } catch (e) {
      console.error(e);
      toast.error("Download failed — check console");
    } finally {
      setDownloading(false);
    }
  };

  // Render just the framed photo (as displayed with zoom/pan) to a PNG
  // data URL using native HTML5 canvas — no html-to-image / no DOM
  // rasterization. This is 100% reliable across iOS Safari, Chrome,
  // and desktop because it re-implements the CSS transform math
  // (`translate(pos.x, pos.y) scale(zoom)` with center-center origin
  // over an `object-fit: contain` image) directly with drawImage().
  const capturePhotoDataUrl = async () => {
    if (!photo) return null;
    const frame = badgeRef.current?.querySelector(
      '[data-testid="badge-photo-frame"]'
    );
    if (!frame) return null;

    // Load the source image at its natural resolution.
    const srcImg = new Image();
    srcImg.src = photo;
    await new Promise((resolve, reject) => {
      if (srcImg.complete && srcImg.naturalWidth > 0) return resolve();
      srcImg.onload = () => resolve();
      srcImg.onerror = () => reject(new Error("photo load failed"));
    });
    if (srcImg.decode) {
      try {
        await srcImg.decode();
      } catch {
        /* ignore — onload already fired */
      }
    }

    const rect = frame.getBoundingClientRect();
    const frameW = rect.width;
    const frameH = rect.height;
    if (!frameW || !frameH) return null;

    const PR = 4; // export pixel ratio → hi-res PNG
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(frameW * PR);
    canvas.height = Math.round(frameH * PR);
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // Frame background (matches the CSS `background: #f1f5f9`).
    ctx.fillStyle = "#f1f5f9";
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.save();
    // Work in CSS pixels, then scale up to canvas pixels.
    ctx.scale(PR, PR);

    // Replicate the CSS transform exactly:
    //   transform: translate(pos.x, pos.y) scale(zoom);
    //   transform-origin: center center;
    // In canvas 2D:
    ctx.translate(photoOffset.x, photoOffset.y);
    ctx.translate(frameW / 2, frameH / 2);
    ctx.scale(photoZoom, photoZoom);
    ctx.translate(-frameW / 2, -frameH / 2);

    // Compute `object-fit: contain` inside the frame.
    const nw = srcImg.naturalWidth;
    const nh = srcImg.naturalHeight;
    const imgAspect = nw / nh;
    const frameAspect = frameW / frameH;
    let dispW;
    let dispH;
    if (imgAspect > frameAspect) {
      dispW = frameW;
      dispH = frameW / imgAspect;
    } else {
      dispH = frameH;
      dispW = frameH * imgAspect;
    }
    const dx = (frameW - dispW) / 2;
    const dy = (frameH - dispH) / 2;
    ctx.drawImage(srcImg, 0, 0, nw, nh, dx, dy, dispW, dispH);

    ctx.restore();

    return canvas.toDataURL("image/png");
  };

  const handleSave = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      toast.error("Please sign in first");
      navigate("/users/admin");
      return;
    }
    if (!fields.idNumber?.trim() || !fields.name?.trim()) {
      toast.error("ID number and Name are required");
      return;
    }
    if (!photo) {
      toast.error("Please upload a photo before saving");
      return;
    }
    setSaving(true);
    try {
      // Always capture the currently framed photo so the saved PNG matches
      // what the user sees (including zoom / drag / lock state).
      const photoDataUrl = await capturePhotoDataUrl();
      const body = {
        id_number: fields.idNumber.trim().toUpperCase(),
        name: fields.name,
        role: fields.role,
        department: fields.department,
        program: fields.program,
        issued: fields.issued,
        expires: fields.expires,
        status: "active",
        photo_data_url: photoDataUrl,
      };
      const res = await fetch(`${API}/badge/employees`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Auth-Token": token,
        },
        body: JSON.stringify(body),
      });
      if (res.status === 401) {
        toast.error("Session expired — sign in again");
        localStorage.removeItem(TOKEN_KEY);
        navigate("/users/admin");
        return;
      }
      if (!res.ok) {
        const err = await res.text();
        toast.error(`Save failed: ${err}`);
        return;
      }
      const saved = await res.json();
      toast.success(`Saved ${saved.id_number}`);
      // Point the badge photo at the freshly saved server URL so future
      // exports / verify page pull the persisted PNG (with cache-bust).
      if (saved.photo_url) {
        setPhoto(`${saved.photo_url}?t=${Date.now()}`);
      }
    } catch (e) {
      toast.error("Save failed — check console");
      console.error(e);
    } finally {
      setSaving(false);
    }
  };

  const update = (k) => (e) =>
    setFields((f) => ({ ...f, [k]: e.target.value }));

  const verifyPayload = `${VERIFY_ORIGIN}/verify?id=${encodeURIComponent(
    fields.idNumber
  )}`;

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="mb-8 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-brand-navy">
            HepCURE Employee Badge
          </h1>
          <p className="text-slate-600 mt-2 text-sm">
            Upload a photo, edit fields, download a 4K print-ready PNG. QR
            code auto-updates and points to the live verification page.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
          {/* Badge preview — locked to Zazzle CR80 portrait print format */}
          <div className="flex justify-center">
            <div
              ref={badgeRef}
              data-testid="badge-preview"
              style={{
                width: BADGE_W,
                height: BADGE_H,
                borderRadius: 22,
                background: "#fff",
                boxShadow:
                  "0 24px 44px -12px rgba(0,0,0,0.28), 0 0 0 1px rgba(15,23,42,0.05)",
                overflow: "hidden",
                fontFamily:
                  "'Inter', system-ui, -apple-system, 'Segoe UI', sans-serif",
                position: "relative",
                display: "flex",
                flexDirection: "column",
              }}
            >
              {/* Top navy band with HepCURE logo + lanyard slot */}
              <div
                data-testid="badge-top-band"
                style={{
                  height: 105,
                  minHeight: 105,
                  flexShrink: 0,
                  background:
                    "linear-gradient(180deg,#1F4E79 0%,#183F63 100%)",
                  position: "relative",
                  display: "flex",
                  alignItems: "flex-end",
                  justifyContent: "center",
                  paddingBottom: 16,
                }}
              >
                <div
                  data-testid="badge-lanyard-slot"
                  style={{
                    position: "absolute",
                    top: 16,
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: 80,
                    height: 14,
                    background: "#fff",
                    borderRadius: 8,
                    boxShadow: "inset 0 2px 3px rgba(0,0,0,0.35)",
                  }}
                />
                <img
                  data-testid="badge-logo"
                  src={LOGO_WHITE}
                  alt="HepCURE"
                  crossOrigin="anonymous"
                  style={{
                    height: 40,
                    width: "auto",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </div>

              {/* Photo section — wide, centered under the header */}
              <div
                style={{
                  padding: "26px 20px 12px 20px",
                  display: "flex",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {(() => {
                  const MAX_W = 280;
                  const MAX_H = 300;
                  const MIN_FRAME_ASPECT = 0.85;
                  const MAX_FRAME_ASPECT = 1.4;
                  const rawA = photoAspect || 1;
                  const a = Math.min(
                    MAX_FRAME_ASPECT,
                    Math.max(MIN_FRAME_ASPECT, rawA)
                  );
                  let frameW = MAX_W;
                  let frameH = MAX_W / a;
                  if (frameH > MAX_H) {
                    frameH = MAX_H;
                    frameW = MAX_H * a;
                  }
                  return (
                    <div
                      onMouseDown={onDragStart}
                      onTouchStart={onDragStart}
                      onWheel={onWheelZoom}
                      data-testid="badge-photo-frame"
                      style={{
                        width: frameW,
                        height: frameH,
                        border: "3px solid #1F4E79",
                        borderRadius: 6,
                        overflow: "hidden",
                        background: "#f1f5f9",
                        cursor: photoLocked
                          ? "default"
                          : isDragging
                          ? "grabbing"
                          : "grab",
                        touchAction: photoLocked ? "auto" : "none",
                        position: "relative",
                      }}
                    >
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          transform: `translate(${photoOffset.x}px, ${photoOffset.y}px) scale(${photoZoom})`,
                          transformOrigin: "center center",
                          transition: isDragging
                            ? "none"
                            : "transform 120ms ease",
                        }}
                      >
                        {photo ? (
                          <img
                            src={photo}
                            alt="Employee"
                            draggable={false}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "contain",
                              objectPosition: "center",
                              display: "block",
                              background: "#f1f5f9",
                              userSelect: "none",
                              pointerEvents: "none",
                            }}
                          />
                        ) : (
                          <div
                            style={{
                              width: "100%",
                              height: "100%",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexDirection: "column",
                              gap: 8,
                              color: "#94a3b8",
                              fontSize: 11,
                              fontWeight: 600,
                              letterSpacing: "0.08em",
                              textTransform: "uppercase",
                              textAlign: "center",
                              padding: 12,
                            }}
                          >
                            <Upload
                              className="w-6 h-6"
                              style={{ opacity: 0.6 }}
                            />
                            Upload a photo
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })()}
              </div>

              {/* Info row — centered text block with QR beside it. This
                  block flex-grows so it fills any remaining vertical space
                  between the photo and the bottom banner, eliminating the
                  "dead white gap" that used to sit in the middle. */}
              <div
                data-testid="badge-info-row"
                style={{
                  padding: "8px 20px 12px 20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 14,
                  flex: "1 0 auto",
                }}
              >
                <div
                  data-testid="badge-info-row-text"
                  style={{
                    textAlign: "center",
                    lineHeight: 1.25,
                    minWidth: 0,
                  }}
                >
                  <div
                    data-testid="badge-name-text"
                    style={{
                      fontSize: 22,
                      fontWeight: 800,
                      color: "#1F4E79",
                      letterSpacing: "-0.01em",
                      lineHeight: 1.15,
                      marginBottom: 6,
                    }}
                  >
                    {fields.name}
                  </div>
                  <div
                    data-testid="badge-dept-text"
                    style={{
                      fontSize: 13,
                      color: "#0f172a",
                      fontWeight: 700,
                      letterSpacing: "0.04em",
                      lineHeight: 1.25,
                    }}
                  >
                    {fields.department}
                  </div>
                </div>

                {/* QR beside the text */}
                <div
                  data-testid="badge-qr-box"
                  style={{
                    background: "#fff",
                    padding: 5,
                    borderRadius: 8,
                    border: "1px solid #cbd5e1",
                    boxShadow:
                      "0 1px 2px rgba(15,23,42,0.08), inset 0 0 0 1px #ffffff",
                    lineHeight: 0,
                    flexShrink: 0,
                  }}
                >
                  <QRCodeSVG
                    data-testid="badge-qr-svg"
                    value={verifyPayload}
                    size={68}
                    level="H"
                    marginSize={2}
                    fgColor="#0f172a"
                    bgColor="#ffffff"
                  />
                </div>
              </div>

              <div
                data-testid="badge-bottom-banner"
                style={{
                  flexShrink: 0,
                  background:
                    "linear-gradient(0deg,#1F4E79 0%,#255F92 100%)",
                  color: "#fff",
                  textAlign: "center",
                  padding: "12px 12px 16px 12px",
                  borderTop: "2px solid #6BA292",
                }}
              >
                <div
                  data-testid="badge-scan-hint"
                  style={{
                    fontSize: 9,
                    color: "rgba(255,255,255,0.75)",
                    letterSpacing: "0.18em",
                    fontWeight: 600,
                    marginBottom: 6,
                  }}
                >
                  SCAN QR TO VERIFY · hepcure.ca/verify
                </div>
                <div
                  data-testid="badge-role-text"
                  style={{
                    fontSize: 21,
                    fontWeight: 800,
                    letterSpacing: "0.18em",
                    lineHeight: 1.15,
                  }}
                >
                  {fields.role}
                </div>
              </div>
            </div>
          </div>

          {/* Editor */}
          <div className="space-y-4">
            <section className="rounded-lg border border-slate-200 bg-white p-5">
              <h2 className="text-sm font-semibold text-slate-900 mb-3">
                Photo
              </h2>
              <div className="flex gap-2">
                <Button
                  data-testid="badge-upload-btn"
                  onClick={() => fileRef.current?.click()}
                  className="flex-1 min-w-0 bg-brand-navy hover:bg-brand-navy-dark text-white"
                >
                  <Upload className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">Upload</span>
                </Button>
                <Button
                  variant="outline"
                  onClick={resetPhoto}
                  data-testid="badge-reset-photo-btn"
                  className="flex-1 min-w-0"
                >
                  <RotateCcw className="w-4 h-4 mr-2 flex-shrink-0" />
                  <span className="truncate">Reset</span>
                </Button>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                onChange={onPhotoUpload}
                style={{ display: "none" }}
                data-testid="badge-photo-input"
              />

              {/* Zoom + reposition controls */}
              <div className="mt-4 space-y-3">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label className="text-xs text-slate-600 flex items-center gap-1">
                      <Maximize2 className="w-3 h-3" />
                      Zoom
                    </Label>
                    <span
                      className="text-xs text-slate-500 tabular-nums"
                      data-testid="badge-zoom-value"
                    >
                      {photoZoom.toFixed(2)}×
                    </span>
                  </div>
                  <Slider
                    data-testid="badge-zoom-slider"
                    min={0.3}
                    max={4}
                    step={0.05}
                    value={[photoZoom]}
                    disabled={photoLocked}
                    onValueChange={(v) => setPhotoZoom(v[0])}
                  />
                </div>
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={resetPhotoTransform}
                    disabled={photoLocked}
                    data-testid="badge-reset-frame-btn"
                    className="text-xs flex-1 min-w-[140px]"
                  >
                    <RotateCcw className="w-3 h-3 mr-1.5" />
                    Reset zoom
                  </Button>
                  <Button
                    variant={photoLocked ? "default" : "outline"}
                    size="sm"
                    onClick={() => setPhotoLocked((v) => !v)}
                    data-testid="badge-lock-toggle"
                    className={
                      photoLocked
                        ? "text-xs flex-1 min-w-[120px] bg-brand-navy hover:bg-brand-navy-dark text-white"
                        : "text-xs flex-1 min-w-[120px]"
                    }
                  >
                    {photoLocked ? (
                      <>
                        <Lock className="w-3 h-3 mr-1.5" />
                        Photo locked
                      </>
                    ) : (
                      <>
                        <Unlock className="w-3 h-3 mr-1.5" />
                        Lock photo
                      </>
                    )}
                  </Button>
                </div>
              </div>

              <p className="text-xs text-slate-500 mt-3">
                Drag the photo with your finger (or mouse) to move it in
                the frame. Zoom below 1× to see the entire source photo,
                or above 1× to close in on the face. Tap <b>Lock photo</b>
                {" "}once you like the framing.
              </p>
            </section>

            <section className="rounded-lg border border-slate-200 bg-white p-5 space-y-3">
              <h2 className="text-sm font-semibold text-slate-900">Details</h2>
              <div>
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-slate-600">
                    Employee ID
                    {editId ? (
                      <span className="ml-1 text-slate-400 font-normal">
                        (locked while editing)
                      </span>
                    ) : (
                      <span className="ml-1 text-slate-400 font-normal">
                        must be unique per employee
                      </span>
                    )}
                  </Label>
                  {!editId && (
                    <button
                      type="button"
                      onClick={() =>
                        setFields((f) => ({
                          ...f,
                          idNumber: generateEmployeeId(),
                        }))
                      }
                      data-testid="badge-input-id-regenerate"
                      className="text-[11px] text-brand-teal hover:underline"
                    >
                      regenerate
                    </button>
                  )}
                </div>
                <Input
                  data-testid="badge-input-idnumber"
                  value={fields.idNumber}
                  onChange={update("idNumber")}
                  readOnly={!!editId}
                  className={`h-9 font-mono tracking-wide ${
                    editId ? "bg-slate-50 text-slate-500" : ""
                  }`}
                  placeholder="HC-XXXX-XX"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Name</Label>
                <Input
                  data-testid="badge-input-name"
                  value={fields.name}
                  onChange={update("name")}
                  className="h-9"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">Department</Label>
                <Input
                  data-testid="badge-input-department"
                  value={fields.department}
                  onChange={update("department")}
                  className="h-9"
                  placeholder="e.g. MOBILE UNIT #2"
                />
              </div>
              <div>
                <Label className="text-xs text-slate-600">
                  Role (bottom banner)
                </Label>
                <Input
                  data-testid="badge-input-role"
                  value={fields.role}
                  onChange={update("role")}
                  className="h-9"
                  placeholder="e.g. OUTREACH WORKER"
                />
              </div>
              <div className="flex gap-4">
                <div className="flex-1 min-w-0">
                  <Label className="text-xs text-slate-600">Issued</Label>
                  <Input
                    type="date"
                    data-testid="badge-input-issued"
                    value={fields.issued}
                    onChange={update("issued")}
                    className="h-9 w-full block"
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <Label className="text-xs text-slate-600">Expires</Label>
                  <Input
                    type="date"
                    data-testid="badge-input-expires"
                    value={fields.expires}
                    onChange={update("expires")}
                    className="h-9 w-full block"
                  />
                </div>
              </div>
            </section>

            <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-xs text-emerald-900 space-y-1">
              <div className="font-semibold">How QR verification works</div>
              <div>
                Scanning the QR opens{" "}
                <span className="font-mono text-[11px]">
                  /verify?id={fields.idNumber}
                </span>{" "}
                and shows one of three statuses (live lookup against the
                HepCURE registry):
              </div>
              <ul className="ml-4 list-disc space-y-0.5">
                <li>
                  <b>Verified</b> — green ✓ with employee details
                </li>
                <li>
                  <b>Expired</b> — red ✗ with expiry date
                </li>
                <li>
                  <b>Not recognized</b> — red ✗ if the ID is not in the system
                </li>
              </ul>
              <div className="pt-1">
                Since expiry is checked live, you never need to reprint a
                badge when a term is renewed.
              </div>
            </div>

            <Button
              data-testid="badge-save-btn"
              onClick={handleSave}
              disabled={saving}
              className="w-full h-11 bg-brand-navy hover:bg-brand-navy-dark text-white font-semibold"
            >
              <Save className="w-4 h-4 mr-2" />
              {saving
                ? "Saving…"
                : editId
                ? "Update employee record"
                : "Save employee record"}
            </Button>

            <Button
              data-testid="badge-download-btn"
              onClick={handleDownload}
              disabled={downloading}
              className="w-full h-11 bg-brand-teal hover:bg-brand-teal-dark text-white font-semibold"
            >
              <Download className="w-4 h-4 mr-2" />
              {downloading ? "Rendering 4K PNG…" : "Download badge (4K PNG)"}
            </Button>

            <Button
              variant="outline"
              onClick={() => navigate("/users/admin")}
              data-testid="badge-open-admin-btn"
              className="w-full h-9"
            >
              <LayoutList className="w-4 h-4 mr-2" />
              Open employee registry
            </Button>

            <p className="text-xs text-slate-500 text-center">
              Saving posts the current photo + fields to the HepCURE
              registry so <span className="font-mono">/verify</span> returns
              this employee&apos;s live record. Sign in required.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
